import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/UI';

/**
 * Welcome Page - Initial onboarding slides for first-time visitors
 * Matches original app's Welcome.screen.js functionality
 */
const WelcomePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);

  interface SlideData {
    id: string;
    background: string;
    title: string;
    description: string;
    instruction?: string;
    showLogo?: boolean;
    icon?: string;
    showPeakFeatures?: boolean;
  }

  const slides: SlideData[] = [
    {
      id: 'intro',
      background: 'https://s3.amazonaws.com/static.marune.social/w_1.jpg',
      title: t('WelcomeScreen.FirstSlide.numberOneApp', '#1 App for Martial Artists') as string,
      description: t('WelcomeScreen.FirstSlide.appDescription', 'Track your training, connect with others, and improve your game') as string,
      instruction: t('WelcomeScreen.FirstSlide.swipeToLearnMore', 'Swipe to learn more') as string,
      showLogo: true,
    },
    {
      id: 'track',
      background: 'https://s3.amazonaws.com/static.marune.social/w_3.jpg',
      icon: 'chart',
      title: t('WelcomeScreen.SecondSlide.trackProgress', 'Track Your Progress') as string,
      description: t('WelcomeScreen.SecondSlide.trackProgressDescription', 'Log every training session, technique, and accomplishment') as string,
    },
    {
      id: 'social',
      background: 'https://s3.amazonaws.com/static.marune.social/w_2.jpg',
      icon: 'users',
      title: t('WelcomeScreen.ThirdSlide.trainTogether', 'Train Together') as string,
      description: t('WelcomeScreen.ThirdSlide.trainTogetherDescription', 'Connect with training partners and share your journey') as string,
    },
    {
      id: 'all-martial-arts',
      background: 'https://s3.amazonaws.com/static.marune.social/w_4.jpg',
      icon: 'globe',
      title: t('WelcomeScreen.ForthSlide.openToAll', 'Open to All Martial Arts') as string,
      description: t('WelcomeScreen.ForthSlide.openToAllDescription', 'BJJ, Judo, Wrestling, MMA, and more') as string,
    },
    {
      id: 'peak',
      background: 'https://s3.amazonaws.com/static.marune.social/peak-bg.jpg',
      title: t('peak.upgradeYourTraining', 'Upgrade Your Training') as string,
      description: t('peak.takeToNextLevel', 'Take your training to the next level with Peak') as string,
      showPeakFeatures: true,
    },
  ];

  const currentSlideData = slides[currentSlide];
  const isLastSlide = currentSlide === slides.length - 1;

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const handlePrevious = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const handleGetStarted = () => {
    // Mark that user has seen the welcome screen
    localStorage.setItem('hasSeenWelcome', 'true');
    navigate('/auth/register');
  };

  const handleLogin = () => {
    // Mark that user has seen the welcome screen
    localStorage.setItem('hasSeenWelcome', 'true');
    navigate('/auth/login');
  };

  return (
    <div className="relative h-screen w-full overflow-hidden">
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center transition-opacity duration-500"
        style={{
          backgroundImage: `url(${currentSlideData.background})`,
        }}
      >
        {/* Dark Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900/60 via-gray-900/70 to-gray-900/90"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col">
        {/* Top Section - Logo or Content */}
        <div className="flex-1 flex flex-col justify-center items-center px-6 pt-12">
          {currentSlideData.showLogo && (
            <div className="mb-8">
              <h1 className="text-5xl md:text-6xl font-bold text-white tracking-tight">
                MARUNE
              </h1>
            </div>
          )}

          {/* Feature Icon (for slides 2-4) */}
          {currentSlideData.icon && (
            <div className="w-full max-w-md mb-8 flex justify-center">
              <FeatureIcon icon={currentSlideData.icon} />
            </div>
          )}

          {/* Peak Features List (for slide 5) */}
          {currentSlideData.showPeakFeatures && (
            <div className="mb-8">
              <div className="inline-block bg-yellow-400 px-8 py-3 rounded-full mb-8">
                <span className="text-3xl font-bold text-blue-900">⭐ PEAK</span>
              </div>

              <div className="space-y-3 text-white">
                <Feature icon="🚫" text={t('peak.benefits.removeAds', 'Remove Ads')} />
                <Feature icon="✓" text={t('peak.benefits.fullWorkoutRoutines', 'Full Workout Routines')} />
                <Feature icon="✓" text={t('peak.benefits.customMetrics', 'Custom Metrics')} />
                <Feature icon="✓" text={t('peak.benefits.setGoals', 'Set Goals')} />
                <Feature icon="✓" text={t('peak.benefits.trackInjuries', 'Track Injuries')} />
                <Feature icon="✓" text={t('peak.benefits.logSparringRounds', 'Log Sparring Rounds')} />
                <Feature icon="..." text={t('peak.benefits.andMore', 'And More')} />
              </div>
            </div>
          )}

          {/* Title and Description */}
          <div className="text-center max-w-lg">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {currentSlideData.title}
            </h2>
            <p className="text-lg text-white/80 leading-relaxed mb-6">
              {currentSlideData.description}
            </p>
            {currentSlideData.instruction && (
              <p className="text-sm text-white/60">
                {currentSlideData.instruction}
              </p>
            )}
          </div>
        </div>

        {/* Bottom Section - Navigation and Buttons */}
        <div className="px-6 pb-8 space-y-4">
          {/* Pagination Dots */}
          <div className="flex justify-center items-center gap-2 mb-4">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentSlide
                    ? 'bg-white w-8'
                    : 'bg-white/40'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button
              onClick={handleGetStarted}
              variant="primary"
              size="lg"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold"
            >
              <span className="flex items-center justify-center gap-2">
                {t('glossary.createAFreeAccount', 'Create a Free Account')}
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </span>
            </Button>

            <div className="text-center">
              <button
                onClick={handleLogin}
                className="text-white hover:text-white/80 transition-colors"
              >
                <span className="text-white/70">
                  {t('WelcomeScreen.FirstSlide.alreadyHaveAnAccount', 'Already have an account?')}{' '}
                </span>
                <span className="font-semibold underline">
                  {t('glossary.signIn', 'Sign In')}
                </span>
              </button>
            </div>
          </div>

          {/* Swipe Navigation (Mobile-friendly) */}
          <div className="flex justify-between items-center mt-4">
            <button
              onClick={handlePrevious}
              disabled={currentSlide === 0}
              className={`p-2 rounded-full ${
                currentSlide === 0
                  ? 'text-white/20 cursor-not-allowed'
                  : 'text-white hover:bg-white/10'
              }`}
              aria-label="Previous slide"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <button
              onClick={handleNext}
              disabled={isLastSlide}
              className={`p-2 rounded-full ${
                isLastSlide
                  ? 'text-white/20 cursor-not-allowed'
                  : 'text-white hover:bg-white/10'
              }`}
              aria-label="Next slide"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Touch Gesture Support for Mobile */}
      <div
        className="absolute inset-0 z-5"
        onTouchStart={(e) => {
          const touch = e.touches[0];
          (e.target as any).touchStartX = touch.clientX;
        }}
        onTouchEnd={(e) => {
          const touch = e.changedTouches[0];
          const touchStartX = (e.target as any).touchStartX;
          const diff = touchStartX - touch.clientX;

          // Swipe left (next)
          if (diff > 50 && !isLastSlide) {
            handleNext();
          }
          // Swipe right (previous)
          else if (diff < -50 && currentSlide > 0) {
            handlePrevious();
          }
        }}
      />
    </div>
  );
};

/**
 * Feature list item for Peak benefits
 */
const Feature: React.FC<{ icon: string; text: string }> = ({ icon, text }) => (
  <div className="flex items-center gap-3">
    <span className="text-xl">{icon}</span>
    <span className="text-base">{text}</span>
  </div>
);

/**
 * Feature icon component for slide visuals
 */
const FeatureIcon: React.FC<{ icon: string }> = ({ icon }) => {
  const iconMap: Record<string, JSX.Element> = {
    chart: (
      <svg className="w-48 h-48 text-white/90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    users: (
      <svg className="w-48 h-48 text-white/90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    globe: (
      <svg className="w-48 h-48 text-white/90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  };

  return iconMap[icon] || null;
};

export default WelcomePage;
